<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { ChevronDown, FileText, Plus, Table2 } from '@lucide/vue';
import type { DocumentFileType } from '../types';

const props = withDefaults(
  defineProps<{
    appearance?: 'primary' | 'secondary' | 'tree' | 'quick';
    disabled?: boolean;
    label?: string;
    description?: string;
  }>(),
  { appearance: 'primary', disabled: false, label: '新建' },
);
const emit = defineEmits<{ select: [fileType: DocumentFileType] }>();
const menu = ref<HTMLDetailsElement | null>(null);
const open = ref(false);

const triggerClass = computed(() => {
  if (props.appearance === 'tree') return 'tree-new new-file-trigger';
  if (props.appearance === 'quick') return 'workspace-action-card new-file-trigger dashboard-file-action';
  return `button button-${props.appearance} new-file-trigger`;
});

function onToggle() {
  open.value = menu.value?.open ?? false;
}

function select(fileType: DocumentFileType) {
  if (props.disabled) return;
  if (menu.value) menu.value.open = false;
  emit('select', fileType);
}

function closeFromOutside(event: PointerEvent) {
  if (menu.value?.open && !menu.value.contains(event.target as Node)) menu.value.open = false;
}

function closeFromKeyboard(event: KeyboardEvent) {
  if (event.key !== 'Escape' || !menu.value?.open) return;
  menu.value.open = false;
  menu.value.querySelector<HTMLElement>('summary')?.focus();
}

onMounted(() => {
  document.addEventListener('pointerdown', closeFromOutside);
  document.addEventListener('keydown', closeFromKeyboard);
});
onBeforeUnmount(() => {
  document.removeEventListener('pointerdown', closeFromOutside);
  document.removeEventListener('keydown', closeFromKeyboard);
});
</script>

<template>
  <details ref="menu" class="new-file-menu" :class="{ 'new-file-menu-tree': appearance === 'tree' }" @toggle="onToggle">
    <summary
      :class="triggerClass"
      :aria-expanded="open"
      :aria-disabled="disabled"
      aria-haspopup="menu"
      :tabindex="disabled ? -1 : 0"
      @click="disabled && $event.preventDefault()"
    >
      <span class="new-file-trigger-icon"><Plus :size="18" aria-hidden="true" /></span>
      <span class="new-file-trigger-copy">
        <strong>{{ label }}</strong>
        <small v-if="description">{{ description }}</small>
      </span>
      <ChevronDown class="new-file-chevron" :size="15" aria-hidden="true" />
    </summary>
    <div class="new-file-popover" role="menu" aria-label="选择文件类型">
      <button type="button" role="menuitem" @click="select('document')">
        <span class="new-file-option-icon new-file-option-document"><FileText :size="19" aria-hidden="true" /></span>
        <span><strong>文档</strong><small>编写说明、方案和团队知识</small></span>
      </button>
      <button type="button" role="menuitem" @click="select('spreadsheet')">
        <span class="new-file-option-icon new-file-option-spreadsheet"><Table2 :size="19" aria-hidden="true" /></span>
        <span><strong>表格</strong><small>按行列整理结构化信息</small></span>
      </button>
    </div>
  </details>
</template>
